// Settings.jsx - Everything an owner can change without a deploy, on five tabs.
//
// The pricing tab contains the payout percentages. Those are the most sensitive
// numbers in the business and they must never appear on the customer site, which
// is why they are served from a route behind settings.read and nothing on the
// public side ever fetches this shape.
//
// LEGEND
//   1. Health      which integrations are actually working, and the webhook
//                  addresses to paste into Surge and EasyPost
//   2. Pricing     percentages, tiers, minimums, and the purity tables
//   3. Company     name, address, contact
//   4. Operations  shipping, vetting, spot override
//   5. Alerts      who hears about what, per person
//   6. Device      installing the app and notifications, per device (Device.jsx)
//   7. Settings    the page

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import clsx from "clsx";

import { useAuth } from "./AdminApp.jsx";
import { useAsync } from "../lib/hooks.js";
import { adminApi } from "../lib/api.js";
import { moneyExact, ago, titleCase, plural, METAL_LABELS, US_STATES } from "../lib/format.js";
import { Button, Panel, Tabs, Input, Textarea, Select, SelectOrType, Toggle, Alert, Loading, Tag, CopyButton, useToast } from "../ui/kit.jsx";
import { AddressField } from "../ui/address.jsx";
import { PROVIDERS, carriersFor, servicesFor, packagesFor, equivalentFor } from "../lib/carriers.js";
import Device from "./Device.jsx";
import { Check, Close, Refresh, Plus, Trash, Shield } from "../ui/icons.jsx";

// The tag in client/index.html, repeated here only so the screen can name it.
// There is no control for it anywhere: a smoke check reads the HTML and fails
// if these two ever disagree.
const MEASUREMENT_ID = "G-NLDVLGKX3R";

////////// 1. HEALTH //////////

function Health({ health, onRefreshSpot, onRan }) {
  if (!health) return null;

  return (
    <div className="space-y-5">
      <LeadImport status={health.leadImport} onRan={onRan} />

      <Panel>
        <p className="eyebrow px-4 py-3 border-b border-rule">Integrations</p>
        <ul className="divide-y divide-rule-soft">
          {Object.entries(health.integrations).map(([key, value]) => (
            <li key={key} className="px-4 py-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[14px] font-medium text-ink capitalize">{titleCase(key)}</p>
                <p className="hint mt-0.5">{value.note}</p>
              </div>
              <span className={clsx("flex items-center gap-1.5 text-[13px] font-semibold shrink-0", value.ready ? "text-positive" : "text-caution")}>
                {value.ready ? <Check size={15} /> : <Close size={15} />}
                {value.ready ? "Ready" : "Not set up"}
              </span>
            </li>
          ))}
        </ul>
      </Panel>

      {/* A webhook aimed at the wrong URL fails in total silence: the provider
          gets a 404, retries, gives up, and nothing in here looks any different.
          So the exact address is printed to copy, and "last text received" is
          the one number that answers whether it is working. */}
      {health.webhooks && (
        <Panel>
          <p className="eyebrow px-4 py-3 border-b border-rule">Webhooks</p>
          <ul className="divide-y divide-rule-soft">
            {Object.entries(health.webhooks).map(([key, hook]) => (
              <li key={key} className="px-4 py-3.5">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
                  <p className="text-[14px] font-medium text-ink">{titleCase(key)}</p>
                  <span className={clsx("flex items-center gap-1.5 text-[12.5px] font-semibold", hook.signed ? "text-positive" : "text-caution")}>
                    {hook.signed ? <Check size={14} /> : <Close size={14} />}
                    {hook.signed ? "Signed" : "Unsigned"}
                  </span>
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 min-w-0 truncate text-[12.5px] bg-surface border border-rule px-2 py-1.5 rounded-xs">
                    {hook.url}
                  </code>
                  <CopyButton value={hook.url} />
                </div>

                <p className="hint mt-2">{hook.note}</p>

                {key === "surge" && (
                  <p className={clsx("text-[12.5px] mt-1.5", hook.lastInboundAt ? "text-muted" : "text-caution font-medium")}>
                    {hook.lastInboundAt
                      ? `Last text received ${ago(hook.lastInboundAt)}, ${hook.inboundCount} in total.`
                      : "No text has ever been received. If customers have replied, this URL is not the one Surge is calling."}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Metal prices</p>
            <p className="text-[14px] text-ink mt-1">
              From {health.spot.source}, {ago(health.spot.at)}
              {health.spot.stale && <Tag tone="caution" className="ml-2">Stale</Tag>}
            </p>
            <p className="hint mt-2 num">
              {Object.entries(health.spot.perGram || {})
                .filter(([, value]) => value > 0)
                .map(([metal, value]) => `${METAL_LABELS[metal]} ${moneyExact(value)}/g`)
                .join("   ·   ")}
            </p>
          </div>
          <Button size="sm" variant="quiet" onClick={onRefreshSpot}><Refresh size={15} /> Fetch now</Button>
        </div>
      </Panel>

      <Panel quiet className="p-4">
        <p className="eyebrow mb-2">Addresses</p>
        <dl className="text-[13px] space-y-1">
          {Object.entries(health.urls).map(([key, value]) => (
            <div key={key} className="flex justify-between gap-4">
              <dt className="text-muted capitalize">{key}</dt>
              <dd className="num text-ink truncate">{value}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-4 pt-2 mt-2 border-t border-rule">
            <dt className="text-muted">Orders</dt>
            <dd className="num text-ink">{health.totals.orders}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Active staff</dt>
            <dd className="num text-ink">{health.totals.users}</dd>
          </div>
        </dl>
      </Panel>
    </div>
  );
}

////////// 1b. THE FACEBOOK LEAD SHEET //////////
// A job that runs every five minutes is invisible until it stops. This is the
// only place that says whether it is alive, so it shows the last run and what
// that run did rather than only whether the credentials exist.

function LeadImport({ status, onRan }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(false);
  if (!status) return null;

  const connect = async () => {
    setLinking(true);
    try {
      const { url } = await adminApi.connectGoogle();
      // A full navigation, not a popup: Google refuses to render its consent
      // screen inside one on some browsers, and a blocked popup here looks
      // exactly like a button that does nothing.
      window.location.assign(url);
    } catch (error) {
      toast.error(error.message);
      setLinking(false);
    }
  };

  const disconnect = async () => {
    setLinking(true);
    try {
      const { revokedAtGoogle } = await adminApi.disconnectGoogle();
      toast.success(revokedAtGoogle ? "Disconnected, and the access was revoked at Google" : "Disconnected here. Google could not be reached, so check that account's third party access.");
      onRan();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLinking(false);
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const summary = await adminApi.runImport();
      if (summary.skipped) toast.info(summary.skipped);
      else if (summary.imported) toast.success(`${summary.imported} imported, ${summary.moved} rows moved`);
      else toast.info(summary.note || "Nothing waiting");
      onRan();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel>
      <div className="px-4 py-3 border-b border-rule flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow">Facebook leads</p>
          <p className="hint mt-1">
            Read from &ldquo;{status.incomingTab}&rdquo; every {plural(status.everyMinutes || 5, "minute")}
            {status.archiveTab ? `, copied to "${status.archiveTab}"` : ""}, then removed.
          </p>
        </div>
        {/* Never disabled. A button that greys out when the thing is not working
            is a button that cannot tell you why it is not working, and the run
            answers with the reason in a sentence. */}
        <Button size="sm" variant="quiet" onClick={run} loading={busy}>
          <Refresh size={15} /> Run now
        </Button>
      </div>

      {/* Signing in beats a service account key in an environment file: it is the
          account that already owns the sheet, and nothing has to be shared with
          an address nobody recognises. */}
      <div className="px-4 py-3 border-b border-rule">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-ink">
              {status.google?.connected ? "Connected to Google" : status.via === "service" ? "Using a service account" : "Not connected to Google"}
            </p>
            <p className="hint mt-0.5 break-words">
              {status.google?.connected
                ? `Signed in as ${status.google.email || "an account Google did not name"}${status.google.connectedAt ? `, ${ago(status.google.connectedAt)}` : ""}.`
                : status.via === "service"
                  ? "From the environment. It only works if the sheet is shared with the address below."
                  : status.oauthReady
                    ? "Sign in as the Google account that already owns the sheet. Nothing needs sharing and nothing expires."
                    : "This server has no OAuth client set up, so there is nothing to sign into yet."}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 shrink-0">
            {status.google?.connected ? (
              <Button size="sm" variant="quiet" onClick={disconnect} loading={linking}>Disconnect</Button>
            ) : (
              <Button size="sm" onClick={connect} loading={linking} disabled={!status.oauthReady}>
                Connect Google
              </Button>
            )}
          </div>
        </div>

        {!status.google?.connected && status.redirectUri && (
          <div className="mt-3">
            <p className="hint mb-1.5">
              {status.oauthReady
                ? "This is the redirect address Google must have on file. It has to match character for character."
                : "When you create the OAuth client in the Google Cloud console, this is the redirect address to give it."}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate text-[12.5px] bg-surface border border-rule px-2 py-1.5 rounded-xs">
                {status.redirectUri}
              </code>
              <CopyButton value={status.redirectUri} />
            </div>
          </div>
        )}

        {status.google?.connected && !status.google.canReadSheets && (
          <Alert tone="warning" className="mt-3">
            That account is connected but did not grant access to spreadsheets, so every run will fail.
            Connect again and accept the sheets permission.
          </Alert>
        )}
      </div>

      <dl className="px-4 py-3 text-[13px] space-y-1.5">
        <div className="flex justify-between gap-4">
          <dt className="text-muted">Status</dt>
          <dd className={clsx("font-semibold", status.ready ? "text-positive" : "text-caution")}>
            {status.running ? "Running now" : status.ready ? "On" : "Off"}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted">Last run</dt>
          <dd className="text-ink text-right">
            {status.lastRunAt ? ago(status.lastRunAt) : "Never"}
            {status.lastOk === false && <Tag tone="critical" className="ml-2">Failed</Tag>}
          </dd>
        </div>
        {status.lastNote && (
          <div className="flex justify-between gap-4">
            <dt className="text-muted shrink-0">Result</dt>
            <dd className="text-ink text-right min-w-0 break-words">{status.lastNote}</dd>
          </div>
        )}
        {status.failures > 0 && (
          <div className="flex justify-between gap-4">
            <dt className="text-muted">Failed runs in a row</dt>
            <dd className="text-critical font-semibold num">{status.failures}</dd>
          </div>
        )}
        {status.serviceAccount && (
          <div className="flex justify-between gap-4 pt-2 mt-2 border-t border-rule-soft">
            <dt className="text-muted shrink-0">Shared with</dt>
            <dd className="num text-ink text-right min-w-0 break-all text-[12px]">{status.serviceAccount}</dd>
          </div>
        )}
      </dl>

      {!status.ready && (
        <p className="hint px-4 pb-4">{status.note}</p>
      )}
    </Panel>
  );
}

////////// 2. PRICING //////////

function PurityTable({ metal, rows, onChange }) {
  const update = (index, patch) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <Panel>
      <p className="eyebrow px-4 py-3 border-b border-rule">{METAL_LABELS[metal]}</p>
      <table className="grid-table stack-table">
        <thead>
          <tr><th>Value</th><th>Label</th><th className="text-right">Factor</th><th /></tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td className="w-24" data-label="Value">
                <input className="field !py-1 !px-2 text-[13px]" value={row.value} onChange={(event) => update(index, { value: event.target.value })} aria-label="Value" />
              </td>
              <td data-label="Label">
                <input className="field !py-1 !px-2 text-[13px]" value={row.label} onChange={(event) => update(index, { label: event.target.value })} aria-label="Label" />
              </td>
              <td className="w-28" data-label="Factor">
                <input
                  className="field !py-1 !px-2 text-[13px] num text-right"
                  type="number" step="0.001" min="0" max="1"
                  value={row.factor}
                  onChange={(event) => update(index, { factor: event.target.value })}
                  aria-label="Factor"
                />
              </td>
              <td className="w-8" data-label="">
                <button type="button" className="btn btn-ghost !px-1" onClick={() => onChange(rows.filter((_, i) => i !== index))} aria-label="Remove">
                  <Trash size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 border-t border-rule-soft">
        <Button size="sm" variant="ghost" onClick={() => onChange([...rows, { value: "", label: "", factor: 0 }])}>
          <Plus size={14} /> Add a purity
        </Button>
      </div>
    </Panel>
  );
}

function Pricing({ draft, set }) {
  const tiers = draft.payout?.volumeTiers || [];
  const setTier = (index, patch) =>
    set("payout.volumeTiers", tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));

  return (
    <div className="space-y-6">
      <Alert tone="warning" title="These numbers never leave this screen">
        <Shield size={14} className="inline mr-1 -mt-0.5" />
        The payout percentages are what we keep. Nothing on the customer site can read them, and the
        calculators there show metal content at spot only.
      </Alert>

      <Panel className="p-5">
        <p className="eyebrow mb-4">Share of metal value we pay</p>
        <div className="grid sm:grid-cols-4 gap-4">
          {["gold", "silver", "platinum", "palladium"].map((metal) => (
            <Input
              key={metal}
              label={METAL_LABELS[metal]}
              type="number"
              min="0"
              max="100"
              step="0.5"
              suffix="%"
              value={draft.payout?.[`${metal}Percent`] ?? ""}
              onChange={(event) => set(`payout.${metal}Percent`, event.target.value)}
            />
          ))}
        </div>
        <p className="hint mt-3">
          Applied to the metal content at spot, then adjusted by the volume tier below.
        </p>
      </Panel>

      <Panel className="p-5">
        <p className="eyebrow mb-4">Volume tiers</p>
        <p className="hint mb-4">
          Larger parcels earn a better rate. The bound is grams of fine metal in the whole order, per metal.
        </p>
        <div className="space-y-2">
          {tiers.map((tier, index) => (
            <div key={index} className="flex items-end gap-3">
              <Input
                label={index === 0 ? "From (fine grams)" : undefined}
                type="number" min="0" step="1"
                value={tier.minFine}
                onChange={(event) => setTier(index, { minFine: event.target.value })}
                wrapClassName="flex-1"
              />
              <Input
                label={index === 0 ? "Multiplier" : undefined}
                type="number" min="0" max="3" step="0.01"
                value={tier.factor}
                onChange={(event) => setTier(index, { factor: event.target.value })}
                wrapClassName="flex-1"
              />
              <button
                type="button"
                className="btn btn-ghost !px-2 mb-1"
                onClick={() => set("payout.volumeTiers", tiers.filter((_, i) => i !== index))}
                aria-label="Remove tier"
              >
                <Trash size={15} />
              </button>
            </div>
          ))}
        </div>
        <Button size="sm" variant="ghost" className="mt-2" onClick={() => set("payout.volumeTiers", [...tiers, { minFine: 0, factor: 1 }])}>
          <Plus size={14} /> Add a tier
        </Button>
      </Panel>

      <Panel className="p-5 grid sm:grid-cols-2 gap-4">
        <Input
          label="Minimum offer"
          type="number" min="0" step="5" prefix="$" className="pl-7"
          hint="Anything below this is flagged for a human instead of quoted automatically."
          value={draft.payout?.minimumOffer ?? ""}
          onChange={(event) => set("payout.minimumOffer", event.target.value)}
        />
        <Input
          label="Offers held for"
          type="number" min="1" max="90" suffix="days"
          hint="After this we requote free at the price on the day."
          value={draft.payout?.offerValidDays ?? ""}
          onChange={(event) => set("payout.offerValidDays", event.target.value)}
        />
      </Panel>

      <Panel className="p-5 space-y-4">
        <div>
          <p className="eyebrow">Affiliates</p>
          <p className="hint mt-1 leading-relaxed measure">
            What somebody earns for sending us a customer. A share of what that customer is paid,
            never of what we made on them.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            label="Commission"
            type="number" min="0" max="100" step="0.5" suffix="%"
            hint="Of the payout on their order. Quoted on Work With Us."
            value={draft.affiliates?.commissionPercent ?? ""}
            onChange={(event) => set("affiliates.commissionPercent", event.target.value)}
          />
          <Input
            label="Most on one order"
            type="number" min="0" step="25" prefix="$" className="pl-7"
            hint="Zero means no cap."
            value={draft.affiliates?.commissionCap ?? ""}
            onChange={(event) => set("affiliates.commissionCap", event.target.value)}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-rule-soft">
          <Input
            label="Kits in a starter set"
            type="number" min="0" max="500"
            hint="The mailers sent free with the display."
            value={draft.affiliates?.startingMailers ?? ""}
            onChange={(event) => set("affiliates.startingMailers", event.target.value)}
          />
          <Input
            label="A mailer after that"
            type="number" min="0" step="0.5" prefix="$" className="pl-7"
            hint="What a restock costs them."
            value={draft.affiliates?.mailerPrice ?? ""}
            onChange={(event) => set("affiliates.mailerPrice", event.target.value)}
          />
        </div>

        <p className="hint leading-relaxed pt-3 border-t border-rule-soft">
          All four numbers are quoted publicly, on the Work With Us page and on the application
          form, so this is the only place any of them is typed. The affiliate terms point to that
          page rather than repeating them.
          {" "}
          Both commission numbers are copied onto an order the moment it comes in, and the copy is
          what gets paid. Changing them here only affects orders that do not exist yet, so an order
          from last month is never quietly repriced.
          {" "}
          <Link to="/admin/affiliates" className="link text-ink">See the affiliates</Link>.
        </p>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        {["gold", "silver", "platinum", "palladium"].map((metal) => (
          <PurityTable
            key={metal}
            metal={metal}
            rows={draft.purities?.[metal] || []}
            onChange={(rows) => set(`purities.${metal}`, rows)}
          />
        ))}
      </div>
    </div>
  );
}

////////// 3. COMPANY //////////

function Company({ draft, set }) {
  return (
    <div className="space-y-5">
      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Business</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Name" value={draft.company?.name ?? ""} onChange={(event) => set("company.name", event.target.value)} />
          <Input label="Short name" value={draft.company?.shortName ?? ""} onChange={(event) => set("company.shortName", event.target.value)} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Email" type="email" value={draft.company?.email ?? ""} onChange={(event) => set("company.email", event.target.value)} />
          <Input label="Phone" value={draft.company?.phone ?? ""} onChange={(event) => set("company.phone", event.target.value)} />
        </div>
        <Input
          label="Support hours"
          hint="Shown on the site and at the bottom of every email."
          value={draft.company?.supportHours ?? ""}
          onChange={(event) => set("company.supportHours", event.target.value)}
        />
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Address packages come to</p>
        <AddressField
          label="Street"
          value={draft.company?.address?.line1 ?? ""}
          onChange={(next) => set("company.address.line1", next)}
          onPick={(address) => {
            for (const key of ["line1", "city", "state", "postal"]) {
              if (address[key]) set(`company.address.${key}`, address[key]);
            }
          }}
        />
        <Input label="Second line" value={draft.company?.address?.line2 ?? ""} onChange={(event) => set("company.address.line2", event.target.value)} />
        <div className="grid grid-cols-3 gap-4">
          <Input label="City" value={draft.company?.address?.city ?? ""} onChange={(event) => set("company.address.city", event.target.value)} />
          <Select
            label="State"
            options={US_STATES.map((code) => ({ value: code, label: code }))}
            value={draft.company?.address?.state ?? ""}
            onChange={(event) => set("company.address.state", event.target.value)}
          />
          <Input label="ZIP" value={draft.company?.address?.postal ?? ""} onChange={(event) => set("company.address.postal", event.target.value)} />
        </div>
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">On the customer site</p>
        <Input
          label="Announcement bar"
          hint="Leave blank to hide it."
          value={draft.site?.announcement ?? ""}
          onChange={(event) => set("site.announcement", event.target.value)}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Trustpilot link" value={draft.site?.trustpilotUrl ?? ""} onChange={(event) => set("site.trustpilotUrl", event.target.value)} />
          <Input label="Google reviews link" value={draft.site?.googleReviewsUrl ?? ""} onChange={(event) => set("site.googleReviewsUrl", event.target.value)} />
          <Input label="Facebook" value={draft.site?.facebookUrl ?? ""} onChange={(event) => set("site.facebookUrl", event.target.value)} />
          <Input label="Instagram" value={draft.site?.instagramUrl ?? ""} onChange={(event) => set("site.instagramUrl", event.target.value)} />
        </div>
        <Input
          label="Evaluation turnaround"
          type="number" min="1" max="720" suffix="hours"
          hint="Quoted in messages and on the tracking page. Promise what you can keep."
          value={draft.site?.evaluationHours ?? ""}
          onChange={(event) => set("site.evaluationHours", event.target.value)}
        />
      </Panel>
    </div>
  );
}

////////// 4. OPERATIONS //////////

// One template per leg, in the order the three of them happen. Every kit is
// bought from these and nobody is asked anything; the dialog on the order is
// where the occasional bigger item goes a different way.
const LABEL_LEGS = [
  { leg: "outbound_kit", title: "Kit out to the customer", note: "An empty padded envelope. A stamp on a flat, not a parcel." },
  { leg: "inbound_items", title: "Prepaid return label", note: "Goes inside the kit. Somebody's gold travels on this one." },
  { leg: "return_items", title: "Items going back", note: "Bought when somebody declines an offer, at our cost." },
];

// One template's four choices, all dropdowns, all in words. Changing the
// provider takes the three fields under it along, because a FedEx service code
// means nothing to Stamps and leaving it there is how a template ends up half
// belonging to a provider that never sees it.
function TemplateFields({ leg, draft, set, stampsReady, fallback }) {
  const template = draft.shipping?.templates?.[leg] || {};
  // The server pins this leg to EasyPost whatever a template says, so offering
  // the choice here would be a control that does nothing.
  const pinned = leg === "inbound_items";
  const provider = pinned ? "easypost" : template.provider ?? "easypost";
  const carrier = template.carrier ?? "";
  const stamps = provider === "stamps";
  const chosen = PROVIDERS.find((entry) => entry.value === provider);

  const write = (field, value) => set(`shipping.templates.${leg}.${field}`, value);

  // The same choice in the new provider's words, rather than four blank boxes.
  // Both sell USPS, so a kit set to First Class Mail at Stamps arrives at
  // EasyPost under its EasyPost name, First, instead of as nothing.
  const setProvider = (next) => {
    const same = equivalentFor(next, { carrier, service: template.service ?? "", package: template.package ?? "" });
    write("provider", next);
    write("carrier", same.carrier);
    write("service", same.service);
    write("package", same.package);
  };

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {stamps && stampsReady === false && (
        <Alert tone={fallback ? "info" : "warning"} className="sm:col-span-2">
          {fallback
            ? "Stamps.com is not connected, so this one is bought from EasyPost on the same USPS service instead. It works and it costs more than a stamp. Connecting Stamps.com is what makes it cheap."
            : "Stamps.com is not connected and the fallback below is off, so this label will refuse to buy. Connect it, pick EasyPost above, or switch the fallback on."}
        </Alert>
      )}

      {pinned ? (
        <Input
          label="Bought from"
          value="EasyPost"
          disabled
          hint="The prepaid return label is the one carrying somebody's gold, so it is always a tracked parcel from EasyPost. A label bought anywhere else goes on the order as a tracking number instead."
          wrapClassName="sm:col-span-2"
        />
      ) : (
      <Select
        label="Bought from"
        options={PROVIDERS}
        hint={chosen?.note}
        value={provider}
        onChange={(event) => setProvider(event.target.value)}
      />
      )}
      <SelectOrType
        label="Carrier"
        options={carriersFor(provider)}
        anyLabel={stamps ? undefined : "Any carrier, price them all"}
        value={carrier}
        onChange={(value) => { write("carrier", value); write("service", ""); write("package", ""); }}
        hint={stamps ? "Stamps only prints USPS" : undefined}
      />
      <SelectOrType
        label="Service"
        options={servicesFor(provider, carrier)}
        anyLabel={stamps ? undefined : "Cheapest they quote"}
        value={template.service ?? ""}
        onChange={(value) => write("service", value)}
        hint={carrier ? undefined : "Pick a carrier first, or leave both blank to price everything"}
        wrapClassName="sm:col-span-2"
      />
      <SelectOrType
        label="Package"
        options={packagesFor(provider, carrier)}
        anyLabel="None, our own packaging"
        value={template.package ?? ""}
        onChange={(value) => write("package", value)}
      />
      {/* An empty box is left out of the save rather than sent as a zero,
          which the schema would refuse and the screen would blame on
          something else. */}
      <Input
        label="Weight"
        type="number"
        min="0.1"
        suffix="oz"
        value={template.weightOz ?? ""}
        onChange={(event) => write("weightOz", event.target.value || undefined)}
      />
    </div>
  );
}

function Operations({ draft, set, health }) {
  // Undefined while health is still loading, which is deliberately not the same
  // as false: a warning that flashes on every page load teaches people to
  // ignore warnings.
  const stampsReady = health?.integrations?.stamps?.ready;
  const fallbackOn = draft.shipping?.fallbackToEasyPost !== false;

  return (
    <div className="space-y-5">
      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Label templates</p>
        <p className="hint">
          What the buy dialog on an order opens with. Every field of one can be changed there for a
          single order, so this is the ordinary kit rather than a rule. An empty service prices
          everything that carrier has. Nothing here is insurance: cover is arranged under our
          own policy and the amount is set per shipment.
        </p>
        {LABEL_LEGS.map(({ leg, title, note }) => (
          <div key={leg} className="pt-4 border-t border-rule-soft">
            <p className="text-[13px] font-semibold text-ink">{title}</p>
            <p className="hint mb-3">{note}</p>
            <TemplateFields leg={leg} draft={draft} set={set} stampsReady={stampsReady} fallback={fallbackOn} />
          </div>
        ))}

        <div className="pt-4 border-t border-rule-soft">
          <Toggle
            label="Buy from EasyPost when Stamps.com is not connected"
            hint="On, a label set to Stamps.com is bought from EasyPost instead, on the same carrier and the same service, so nobody is stopped by an integration that is not set up yet. It costs more than a stamp. If EasyPost will not quote that service the purchase fails and says which ones it did quote, rather than buying something else. Off, it refuses straight away. Either way the shipment records what happened."
            checked={fallbackOn}
            onChange={(value) => set("shipping.fallbackToEasyPost", value)}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-rule-soft">
          <Select
            label="Label format"
            hint="PNG prints the kit pair sheet. ZPL for a thermal printer."
            options={[
              { value: "PNG", label: "PNG, for the pair sheet" },
              { value: "PDF", label: "PDF" },
              { value: "ZPL", label: "ZPL, thermal" },
              { value: "EPL2", label: "EPL2, thermal" },
            ]}
            value={draft.shipping?.labelFormat ?? "PNG"}
            onChange={(event) => set("shipping.labelFormat", event.target.value)}
          />
                  </div>
        <Toggle
          label="Print a confirmation sheet after the labels"
          hint="A third print with both tracking numbers and who printed them, for the file. Off keeps the run to two dialogs."
          checked={Boolean(draft.shipping?.printConfirmation)}
          onChange={(value) => set("shipping.printConfirmation", value)}
        />
      </Panel>

      <Panel className="p-5 space-y-3">
        <p className="eyebrow">Advertising measurement</p>
        <p className="hint leading-relaxed">
          There is nothing to set here. The Google tag{" "}
          <span className="num">{MEASUREMENT_ID}</span> is written into the customer site's HTML, so it
          loads from the first byte on every page and on the prerendered ones a crawler sees. It reports
          to Google Analytics, and the Google Ads conversion is imported from there.
        </p>
        <p className="hint leading-relaxed">
          Five events are sent: <span className="num">form_start</span>,{" "}
          <span className="num">form_step</span>, <span className="num">generate_lead</span>,{" "}
          <span className="num">tool_used</span> and{" "}
          <span className="num">price_alert_created</span>. Mark{" "}
          <span className="num">generate_lead</span> as the key event in Analytics and build the Ads
          conversion from it; it carries <span className="num">form_name</span> and the order number.
          Page views are Analytics' own enhanced measurement, so the site sends none of its own, and
          the tag is never configured on the dashboard or for anybody whose browser sends Global
          Privacy Control or Do Not Track.
        </p>
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Cover on a shipment</p>
        <p className="hint leading-relaxed">
          What we take liability for on a package coming to us. This is our own cover and has nothing to do
          with what a carrier sells. The standard applies to every shipment with no form and no conversation.
          Anything above it has to be asked for on the public form and approved before the items are posted.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Standard, on every shipment" type="number" prefix="$" className="pl-7" value={draft.coverage?.standard ?? ""} onChange={(event) => set("coverage.standard", event.target.value)} />
          <Input label="Most we will ever approve" type="number" prefix="$" className="pl-7" value={draft.coverage?.maximum ?? ""} onChange={(event) => set("coverage.maximum", event.target.value)} />
        </div>
        <Toggle
          label="Take requests for higher cover"
          hint="Off closes the form and says so on the page. The standard amount is unaffected."
          checked={draft.coverage?.requests !== false}
          onChange={(value) => set("coverage.requests", value)}
        />
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Vetting</p>
        <Toggle
          label="Vet requests before sending a kit"
          hint="Off means every request is approved automatically."
          checked={draft.vetting?.enabled ?? true}
          onChange={(value) => set("vetting.enabled", value)}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            label="Auto approve above"
            type="number" min="0" max="100"
            hint="Out of 100. Anything lower waits for a person."
            value={draft.vetting?.autoApproveScore ?? ""}
            onChange={(event) => set("vetting.autoApproveScore", event.target.value)}
          />
          <Input
            label="Minimum estimated value"
            type="number" min="0" prefix="$" className="pl-7"
            hint="Below this a kit is probably not worth sending."
            value={draft.vetting?.minEstimatedValue ?? ""}
            onChange={(event) => set("vetting.minEstimatedValue", event.target.value)}
          />
        </div>
        <Input
          label="States we do not ship to"
          hint="Two letter codes, separated by commas. Usually empty."
          value={(draft.vetting?.blockedStates || []).join(", ")}
          onChange={(event) => set("vetting.blockedStates", event.target.value.split(",").map((code) => code.trim().toUpperCase()).filter(Boolean))}
        />
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Unfinished kit forms</p>
        <Toggle
          label="Keep forms people start but never send"
          hint="Saved once somebody has given an email or a phone number, and never before. They appear under Bounced forms."
          checked={draft.leads?.capture ?? true}
          onChange={(value) => set("leads.capture", value)}
        />
        {draft.leads?.capture !== false && (
          <>
            <Input
              label="Delete them after"
              type="number" min="1" max="730" suffix="days"
              hint="These people never agreed to anything, so the record deletes itself. Ninety days is a sensible default."
              value={draft.leads?.retentionDays ?? ""}
              onChange={(event) => set("leads.retentionDays", event.target.value)}
            />

            <div className="pt-4 border-t border-rule-soft space-y-4">
              <Toggle
                label="Send one reminder email automatically"
                hint="One email, once, to anybody who gave an email address and left. Never a sequence, never a text, and never to somebody who has unsubscribed or already has an order."
                checked={draft.leads?.autoEmail ?? true}
                onChange={(value) => set("leads.autoEmail", value)}
              />

              {draft.leads?.autoEmail !== false && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      label="Wait before sending"
                      type="number" min="0" max="168" suffix="hours"
                      hint="Long enough that they have gone, not gone to find their glasses."
                      value={draft.leads?.autoEmailAfterHours ?? ""}
                      onChange={(event) => set("leads.autoEmailAfterHours", event.target.value)}
                    />
                    <Input
                      label="Give up after"
                      type="number" min="1" max="90" suffix="days"
                      hint="A reminder that arrives later than this reads as spam, so it is not sent at all."
                      value={draft.leads?.autoEmailWindowDays ?? ""}
                      onChange={(event) => set("leads.autoEmailWindowDays", event.target.value)}
                    />
                  </div>
                  <Input
                    label="Most to send in one sweep"
                    type="number" min="1" max="200"
                    hint="The job runs every fifteen minutes. This caps what happens if it is switched on with a backlog waiting."
                    value={draft.leads?.autoEmailPerRun ?? ""}
                    onChange={(event) => set("leads.autoEmailPerRun", event.target.value)}
                  />
                  <Alert tone="info">
                    The reminder carries a one click unsubscribe and our postal address, which is what
                    makes it a lawful commercial email. Anyone who unsubscribes is suppressed by email
                    address for good, across every future form.
                  </Alert>
                </>
              )}
            </div>
          </>
        )}
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Metal prices</p>
        <Toggle
          label="Use fixed prices instead of the live feed"
          hint="Switch this on only if the feed is down and you need to keep quoting."
          checked={draft.spot?.overrideEnabled ?? false}
          onChange={(value) => set("spot.overrideEnabled", value)}
        />
        {draft.spot?.overrideEnabled && (
          <div className="grid sm:grid-cols-4 gap-4">
            {["gold", "silver", "platinum", "palladium"].map((metal) => (
              <Input
                key={metal}
                label={`${METAL_LABELS[metal]} per gram`}
                type="number" step="0.01" prefix="$" className="pl-7"
                value={draft.spot?.[metal] ?? ""}
                onChange={(event) => set(`spot.${metal}`, event.target.value)}
              />
            ))}
          </div>
        )}
        <Input
          label="Refresh the live price every"
          type="number" min="1" max="1440" suffix="min"
          value={draft.spot?.refreshMinutes ?? ""}
          onChange={(event) => set("spot.refreshMinutes", event.target.value)}
        />
      </Panel>
      <Panel className="p-5 space-y-4">
        <div>
          <p className="eyebrow">Facebook lead sheet</p>
          <p className="hint mt-1 leading-relaxed">
            Rows are read on the interval below and deleted once the order exists. A row is never
            removed before that, so a failure leaves it in the sheet for the next run rather than
            losing it. Connect the Google account on the Health tab.
          </p>
        </div>

        <Input
          label="Which sheet"
          hint="Paste the whole address from your browser. Everything but the id is thrown away."
          placeholder="https://docs.google.com/spreadsheets/d/..."
          value={draft.leadImport?.spreadsheetId ?? ""}
          onChange={(event) => set("leadImport.spreadsheetId", event.target.value)}
        />

        <Input
          label="Check for new rows every"
          type="number" min="1" max="1440"
          suffix="min"
          hint="Takes effect on the next check, without a restart. One minute is the floor; a campaign that runs once a week does not need to be asked every five."
          value={draft.leadImport?.everyMinutes ?? ""}
          onChange={(event) => set("leadImport.everyMinutes", event.target.value)}
        />

        <div className="grid sm:grid-cols-2 gap-4">
          <Input
            label="Rows arrive on"
            value={draft.leadImport?.incomingTab ?? ""}
            onChange={(event) => set("leadImport.incomingTab", event.target.value)}
          />
          <Input
            label="Copy to, before deleting"
            hint="Optional. Leave empty to just delete the row."
            value={draft.leadImport?.archiveTab ?? ""}
            onChange={(event) => set("leadImport.archiveTab", event.target.value)}
          />
        </div>

        <Input
          label="Rows per run"
          type="number" min="1" max="500"
          hint="A backlog drains over several runs rather than arriving all at once."
          value={draft.leadImport?.perRun ?? ""}
          onChange={(event) => set("leadImport.perRun", event.target.value)}
        />

        <Toggle
          label="Import switched on"
          hint="Off stops the job without touching anything in the sheet."
          checked={draft.leadImport?.enabled ?? true}
          onChange={(value) => set("leadImport.enabled", value)}
        />

        <Toggle
          label="Email an imported customer"
          hint="Off by default. Turning it on cannot message a backlog: anything older than the age below is never written to."
          checked={draft.leadImport?.notifyCustomer ?? false}
          onChange={(value) => set("leadImport.notifyCustomer", value)}
        />

        <Input
          label="Never message a row older than"
          type="number" min="1" max="720" suffix="hours"
          value={draft.leadImport?.notifyMaxAgeHours ?? ""}
          onChange={(event) => set("leadImport.notifyMaxAgeHours", event.target.value)}
        />

        <Toggle
          label="The Facebook form asks for text consent"
          hint="What the form actually says decides this. Switch it off and imported customers are never texted."
          checked={draft.leadImport?.smsConsent ?? true}
          onChange={(value) => set("leadImport.smsConsent", value)}
        />

        <Textarea
          label="The consent wording the form shows"
          rows={3}
          hint="Stored with every imported order, so the record says what the person actually agreed to. Change the form, change this."
          value={draft.leadImport?.consentPrompt ?? ""}
          onChange={(event) => set("leadImport.consentPrompt", event.target.value)}
        />
      </Panel>
    </div>
  );
}

////////// 5. ALERTS //////////

// Three channels wide and ten events tall is a matrix, and a matrix of 24 pixel
// checkboxes is unusable on a phone. Each channel is a labelled button instead:
// it says what it is, it is big enough to hit, and the row reflows to two lines
// on a narrow screen rather than shrinking.

const CHANNELS = [
  { key: "email", label: "Email" },
  { key: "sms", label: "Text" },
  { key: "push", label: "Push" },
];

function Alerts({ notifyEvents, subscriptions, onChange, draft, set, pushReady }) {
  return (
    <div className="space-y-5">
      <Panel>
        <div className="px-4 py-3 border-b border-rule">
          <p className="eyebrow">What you personally hear about</p>
          <p className="hint mt-1">Each person has their own settings. This changes yours only.</p>
        </div>

        <ul className="divide-y divide-rule-soft">
          {notifyEvents.map((event) => {
            const channels = subscriptions?.[event.key] || [];
            const toggle = (channel) => {
              const next = channels.includes(channel)
                ? channels.filter((entry) => entry !== channel)
                : [...channels, channel];
              onChange({ ...subscriptions, [event.key]: next });
            };

            return (
              <li key={event.key} className="px-4 py-3 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
                <span className="text-[14px] text-ink">{event.label}</span>
                <span className="flex gap-1.5 shrink-0">
                  {CHANNELS.map((channel) => {
                    const on = channels.includes(channel.key);
                    return (
                      <button
                        key={channel.key}
                        type="button"
                        onClick={() => toggle(channel.key)}
                        aria-pressed={on}
                        aria-label={`${event.label} by ${channel.label}`}
                        className={clsx(
                          "btn btn-sm !px-3 !min-h-[2.25rem] gap-1.5",
                          on ? "btn-ink" : "btn-quiet",
                          channel.key === "push" && !pushReady && "opacity-45"
                        )}
                      >
                        {on && <Check size={13} />}
                        {channel.label}
                      </button>
                    );
                  })}
                </span>
              </li>
            );
          })}
        </ul>

        <p className="hint px-4 py-3 border-t border-rule-soft leading-relaxed">
          {pushReady
            ? "Push arrives on the lock screen of any device you turned notifications on for, under This device."
            : "Push is switchable here but nothing is configured on the server yet, so nothing will arrive. See This device."}
        </p>
      </Panel>

      <Panel className="p-5 space-y-4">
        <p className="eyebrow">Shared addresses</p>
        <p className="hint">
          These get every notification regardless of who is signed in. Use them for a shared inbox or an
          on-call phone, not for people who have their own account.
        </p>
        <Input
          label="Always email"
          hint="Comma separated"
          value={(draft.notifications?.alwaysEmail || []).join(", ")}
          onChange={(event) => set("notifications.alwaysEmail", event.target.value.split(",").map((entry) => entry.trim()).filter(Boolean))}
        />
        <Input
          label="Always text"
          hint="Comma separated"
          value={(draft.notifications?.alwaysSms || []).join(", ")}
          onChange={(event) => set("notifications.alwaysSms", event.target.value.split(",").map((entry) => entry.trim()).filter(Boolean))}
        />
      </Panel>
    </div>
  );
}

////////// 7. PAGE //////////

const setDeep = (object, path, value) => {
  const keys = path.split(".");
  const next = { ...object };
  let cursor = next;
  for (let index = 0; index < keys.length - 1; index += 1) {
    cursor[keys[index]] = { ...(cursor[keys[index]] || {}) };
    cursor = cursor[keys[index]];
  }
  cursor[keys.at(-1)] = value;
  return next;
};

export default function Settings() {
  const toast = useToast();
  const { user, notifyEvents, can, reload: reloadSession } = useAuth();

  const { data, loading, error, reload } = useAsync(() => adminApi.settings(), []);
  const { data: health, reload: reloadHealth } = useAsync(() => adminApi.health(), []);

  // The tab is in the URL so the sidebar can link straight at "this device",
  // and so a link to a settings screen still lands on the right one.
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "health";
  const setTab = (next) => setParams(next === "health" ? {} : { tab: next }, { replace: true });
  const [draft, setDraft] = useState(null);
  const [subscriptions, setSubscriptions] = useState(user.subscriptions || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data?.settings) setDraft(data.settings); }, [data]);

  if (loading || !draft) return <Loading label="Loading settings" />;
  if (error) return <div className="p-8"><Alert tone="error">{error.message}</Alert></div>;

  const set = (path, value) => setDraft((previous) => setDeep(previous, path, value));

  const save = async () => {
    setSaving(true);
    try {
      if (can("settings.write")) {
        // The whole draft, not a hand written list of the parts of it.
        //
        // This used to name each block, and three of them were missing:
        // measurement, coverage and price alerts were all typed into the form,
        // held in state, and then dropped on the way out. The screen said
        // "Saved" every time, because it had.
        //
        // The server's own schema is the authority on what may be written and
        // it strips anything else, including _id and the timestamps, so there
        // is nothing for a second list here to add except a way to disagree.
        await adminApi.saveSettings(draft);
      }
      await adminApi.saveSubscriptions(subscriptions);
      await Promise.all([reload(), reloadSession()]);
      toast.success("Saved");
    } catch (problem) {
      // The whole draft is sent, so the field that fails is often on another tab
      // and nothing on this one lights up. "Please check the highlighted fields"
      // with nothing highlighted is exactly how an empty archive tab blocked
      // every save for weeks without anybody being able to say why. Name it.
      const fields = Object.entries(problem.fields || {});
      if (fields.length) {
        const words = (path) => path.split(".")
          .map((part) => part.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase())
          .join(": ");
        toast.error(`Not saved. ${fields.map(([path, why]) => `${words(path)}, ${String(why).toLowerCase()}`).join("; ")}.`);
      } else {
        toast.error(problem.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const refreshSpot = async () => {
    try {
      await adminApi.refreshSpot();
      await reloadHealth();
      toast.success("Prices refreshed");
    } catch (problem) {
      toast.error(problem.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="display display-lg text-ink">Settings</h1>
          <p className="hint mt-1">Changes take effect immediately, on every order.</p>
        </div>
        <Button onClick={save} loading={saving} className="w-full xs:w-auto">Save changes</Button>
      </header>

      <Tabs
        tabs={[
          { value: "health", label: "Status" },
          { value: "pricing", label: "Pricing" },
          { value: "company", label: "Company" },
          { value: "operations", label: "Operations" },
          { value: "alerts", label: "My alerts" },
          { value: "device", label: "This device" },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-6 -mx-4 px-4 sm:mx-0 sm:px-0"
      />

      {!can("settings.write") && !["alerts", "health", "device"].includes(tab) && (
        <Alert tone="info" className="mb-4">
          You can see these but not change them. Ask an owner or manager.
        </Alert>
      )}

      {tab === "health" && <Health health={health} onRefreshSpot={refreshSpot} onRan={reloadHealth} />}
      {tab === "pricing" && <Pricing draft={draft} set={set} />}
      {tab === "company" && <Company draft={draft} set={set} />}
      {tab === "operations" && <Operations draft={draft} set={set} health={health} />}
      {tab === "device" && <Device />}
      {tab === "alerts" && (
        <Alerts
          notifyEvents={notifyEvents}
          subscriptions={subscriptions}
          onChange={setSubscriptions}
          draft={draft}
          set={set}
          pushReady={Boolean(health?.integrations?.push?.ready)}
        />
      )}
    </div>
  );
}
